import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import { DemandDTORuntype } from '../negotiation';
import { LoadingOffersProgressDTORuntype } from '../part/partBackendTypes';
import { UserDTORuntype } from '../user';
import {
    CustomerPortalStateRuntype,
    ProjectAreaRuntype,
    ProjectProgressChangeRuntype,
    ProjectProgressEntryRuntype,
    ProjectProgressRuntype,
    QuotationBuilderDataResponseRuntype,
    QuotationBuilderStateResponseRuntype,
    QuotationWarningsRuntype,
    RfqDTORuntype,
    RfqInputDTORuntype,
    RfqListItemRuntype,
    RfqStatusHistoryRuntype,
    RfqUpdateRuntype,
    SaveQuotationBuilderStateRequestRuntype,
} from './rfqBackendTypes';
import { SnapshotMetadataItemRuntype, SnapshotTagRuntype } from './snapshotRuntype';

// Split into groups with an explicit type: the inferred one exceeds what TypeScript can serialize.
const rfqListEndpoints = {
    'GET /rfqs': endpoint({
        description: 'GET multiple RfQs',
        pathParams: z.undefined(),
        queryParams: z.union([
            z.object({ activity_id: z.string() }),
            z.object({}),
            z.object({ expense_id: z.string() }),
            z.object({ filter: z.enum(['all', 'archived', 'not-archived']), customer_id: z.string().optional() }),
        ]),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.array(RfqListItemRuntype),
            total_pages: z.number(),
        }),
        actionPermission: () => 'CanGetRfq',
    }),

    'POST /rfqs': endpoint({
        description: 'Create an RfQ',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: RfqInputDTORuntype,
        responseBody: z.object({ data: RfqDTORuntype }),
        invalidates: ['GET /rfqs', 'GET /rfqs/:rfqId', 'GET /organization-settings/auto-rfq-number/next'],
        actionPermission: () => 'CanCreateRfq',
    }),

    'GET /rfqs/:rfqId': endpoint({
        description: 'GET a single RfQ',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: RfqDTORuntype,
        }),
        actionPermission: () => 'CanGetRfq',
    }),
};

const rfqSnapshotEndpoints = {
    'GET /rfqs/:rfqId/snapshot': endpoint({
        description: 'Fetch a list of snapshot metadata items',
        pathParams: z.object({
            rfqId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            items: z.array(SnapshotMetadataItemRuntype),
        }),
    }),

    'GET /rfqs/:rfqId/demands': endpoint({
        description: 'Fetch a list of demands for a single RfQ',
        pathParams: z.object({
            rfqId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            items: z.array(DemandDTORuntype),
        }),
    }),

    'POST /rfqs/:rfqId/snapshot/trigger': endpoint({
        description: 'Create a sourcing snapshot',
        pathParams: z.object({
            rfqId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.object({
            tags: z.array(z.unknown()),
        }),
        responseBody: z.unknown(),
        invalidates: ['GET /rfqs/:rfqId/snapshot'],
    }),

    'PATCH /rfqs/:rfqId/snapshot/:snapshotId': endpoint({
        description: 'Update a snapshot',
        pathParams: z.object({
            rfqId: z.string(),
            snapshotId: z.string(),
        }),
        queryParams: z.undefined(),
        requestBody: z.object({
            tags: z.array(SnapshotTagRuntype),
        }),
        responseBody: z.unknown(),
        invalidates: ['GET /rfqs/:rfqId/snapshot'],
    }),
};

const rfqUpdateEndpoints = {
    'POST /rfqs/:rfqId/update-offers': endpoint({
        description: 'Updates all the offers in an RfQ',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({ since: z.string() }),
        responseBody: z.object({ since: z.string() }),
        invalidates: ['GET /panels'],
    }),

    'GET /sourcing/progress/:rfqId': endpoint({
        description: 'Fetches the number of orders still being updated',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: LoadingOffersProgressDTORuntype,
    }),

    'PATCH /rfqs/:rfqId': endpoint({
        description: 'PATCH a single RfQ',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: RfqUpdateRuntype,
        responseBody: z.object({}),
        invalidates: ['GET /rfqs', 'GET /rfqs/:rfqId', 'GET /order-management/order-selection-options'],
        actionPermission: () => 'CanEditRfq',
    }),

    'PATCH /rfqs/:rfqId/archive': endpoint({
        description: 'Archive or unarchive a single RfQ',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({}),
        invalidates: ['GET /rfqs', 'GET /rfqs/:rfqId'],
        actionPermission: () => 'CanArchiveRfq',
    }),

    'PATCH /rfqs/:rfqId/unarchive': endpoint({
        description: 'Archive or unarchive a single RfQ',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({}),
        invalidates: ['GET /rfqs', 'GET /rfqs/:rfqId'],
        actionPermission: () => 'CanArchiveRfq',
    }),

    'DELETE /rfqs/:rfqId': endpoint({
        description: 'Deletes a single RfQ',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            deleted: z.number(),
        }),
        invalidates: ['GET /rfqs'],
        removes: ['GET /rfqs/:rfqId'],
        actionPermission: () => 'CanDeleteRfq',
    }),

    'PATCH /rfqs/:rfqId/project-progress': endpoint({
        description: 'Update the project progress for a single RfQ area',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({
            area: ProjectAreaRuntype,
            progress: ProjectProgressRuntype,
        }),
        responseBody: z.unknown(),
        invalidates: [
            'GET /rfqs/project-progress',
            'GET /rfqs/:rfqId/project-progress',
            'GET /rfqs/project-progress/history',
        ],
        actionPermission: () => 'CanEditProjectProgress',
    }),

    'GET /rfqs/project-progress': endpoint({
        description: 'List project progress for every RfQ in the tenant',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(ProjectProgressEntryRuntype) }),
        actionPermission: () => 'CanViewProjectProgress',
    }),

    'GET /rfqs/:rfqId/project-progress': endpoint({
        description: 'List project progress for a single RfQ',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(ProjectProgressEntryRuntype) }),
        actionPermission: () => 'CanViewProjectProgress',
    }),
};

const rfqContributorEndpoints = {
    'GET /rfqs/:rfqId/possible-contributors': endpoint({
        description: 'Returns a list of all possible contributors on a single RFQ',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(UserDTORuntype) }),
    }),

    'POST /rfqs/:rfqId/technical-support-request': endpoint({
        description: 'Requests technical support from the EMS',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        // Backend responds with `200 OK` and an empty body, which the http client reads as an empty string.
        responseBody: z.string(),
    }),
};

const rfqQuotationEndpoints = {
    'POST /rfqs/:rfqId/edit-quotation-request': endpoint({
        description: 'Requests to edit quotation from the EMS',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        // Backend responds with `200 OK` and an empty body, which the http client reads as an empty string.
        responseBody: z.string(),
    }),

    'PATCH /rfqs/:rfqId/request-quotation': endpoint({
        description: 'As a customer request a quotation from the EMS',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string(),
        invalidates: ['GET /rfqs', 'GET /rfqs/:rfqId'],
    }),

    'POST /rfqs/:rfqId/customer-portal': endpoint({
        description:
            'Get the state of the customer portal as a customer. Potentially sets the RfQ status to QuotationAvailable when the workflow is automatic and the RfQ is ready for quotation.',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: CustomerPortalStateRuntype,
        invalidates: ['GET /rfqs', 'GET /rfqs/:rfqId'],
    }),

    'GET /rfqs/:rfqId/quotation/warnings': endpoint({
        description: 'Get warnings indicating if it is okay for an EMS to update the RfQ status to QuotationAvailable',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: QuotationWarningsRuntype,
    }),

    'GET /rfqs/:rfqId/quotation-builder-data': endpoint({
        description:
            'Bundle of all data needed to render the quotation builder for an RfQ: RfQ + customer + ' +
            'company info, plus assemblies with their sourcing scenarios and costed-BOM components.',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: QuotationBuilderDataResponseRuntype,
        actionPermission: () => 'CanGetRfq',
    }),

    'GET /rfqs/:rfqId/quotation-builder-state': endpoint({
        description:
            'The persisted quotation builder snapshot (template body, edited data, inputs and section ' +
            'toggles) for an RfQ. Null when no quotation has been generated from a template yet.',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: QuotationBuilderStateResponseRuntype.nullable(),
        actionPermission: () => 'CanGetQuotationFile',
    }),

    'PUT /rfqs/:rfqId/quotation-builder-state': endpoint({
        description:
            'Persist (upsert) the quotation builder snapshot for an RfQ so the generated quotation can ' +
            'be reopened and edited instead of being rebuilt from scratch.',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: SaveQuotationBuilderStateRequestRuntype,
        responseBody: QuotationBuilderStateResponseRuntype,
        invalidates: ['GET /rfqs/:rfqId/quotation-builder-state'],
        actionPermission: () => 'CanUploadQuotationFile',
    }),

    'DELETE /rfqs/:rfqId/quotation-builder-state': endpoint({
        description: 'Delete the persisted quotation builder snapshot for an RfQ (no-op when none exists).',
        pathParams: z.object({ rfqId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.null(),
        invalidates: ['GET /rfqs/:rfqId/quotation-builder-state'],
        actionPermission: () => 'CanDeleteQuotationFile',
    }),

    'GET /rfqs/status-history': endpoint({
        description:
            'Get the RfQ status change history for the whole tenant. Returns a flat list of status changes (one entry per change), each tagged with its rfq_id. Takes no parameters.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(RfqStatusHistoryRuntype),
    }),

    'GET /rfqs/project-progress/history': endpoint({
        description: 'Tenant-wide audit log of RfQ project progress transitions',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(ProjectProgressChangeRuntype),
        actionPermission: () => 'CanViewProjectProgress',
    }),
};

const gatewayEndpoints = {
    'GET /gateway/test': endpoint({
        description: 'Gateway',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.unknown(),
    }),
};

type InnerRfqListEndpoints = typeof rfqListEndpoints;
type InnerRfqSnapshotEndpoints = typeof rfqSnapshotEndpoints;
type InnerRfqUpdateEndpoints = typeof rfqUpdateEndpoints;
type InnerRfqContributorEndpoints = typeof rfqContributorEndpoints;
type InnerRfqQuotationEndpoints = typeof rfqQuotationEndpoints;
type InnerGatewayEndpoints = typeof gatewayEndpoints;

interface RfqEndpointsRegistry
    extends
        InnerRfqListEndpoints,
        InnerRfqSnapshotEndpoints,
        InnerRfqUpdateEndpoints,
        InnerRfqContributorEndpoints,
        InnerRfqQuotationEndpoints,
        InnerGatewayEndpoints {}

export const rfqEndpoints: RfqEndpointsRegistry = {
    ...rfqListEndpoints,
    ...rfqSnapshotEndpoints,
    ...rfqUpdateEndpoints,
    ...rfqContributorEndpoints,
    ...rfqQuotationEndpoints,
    ...gatewayEndpoints,
};
